// The quick-reply row under a message: a one-line box that grows to three lines, Send, and Open
// task. Shared by the desktop toast and the My tasks card. Enter sends, Shift+Enter starts a new
// line, Escape hands back to the caller without sending (a toast closes). The draft is kept per
// delivery for the page session (`reply-drafts.ts`), so closing a toast never loses what was typed.
// One send at a time: while it runs the button is disabled and another Enter is ignored. A failed
// send keeps the draft and offers Try again; a sent reply is spent and the caller is told.
import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  type KeyboardEvent,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  keepReplyDraft,
  replyDraft,
  spendReplyDraft,
} from '../model/reply-drafts';
import * as styles from './quick-reply.css';

/** How long "Sent" shows before a toast closes. */
export const QUICK_REPLY_CLOSE_MS = 1_500;

type SendState = 'idle' | 'sending' | 'sent' | 'failed';

export interface QuickReplyProps {
  /** The delivery's id: the key its draft is kept under. */
  draftKey: string;
  /** Who reads the reply, for the box's label. */
  to: string;
  /** Posts the reply; resolves `ok: false` on any failure (it never needs to throw). */
  send: (text: string) => Promise<{ ok: boolean }>;
  /** After a reply was sent. */
  onSent?: () => void;
  /** Escape in the box. */
  onEscape?: () => void;
  onOpen: () => void;
}

export function QuickReply({
  draftKey,
  to,
  send,
  onSent,
  onEscape,
  onOpen,
}: QuickReplyProps) {
  const t = useI18n();
  const id = useId();
  const [draft, setDraft] = useState(() => replyDraft(draftKey));
  const [state, setState] = useState<SendState>('idle');
  const inFlight = useRef(false);
  const box = useRef<HTMLTextAreaElement>(null);

  // Grow with the text; the style's maximum (three lines) then scrolls.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.blockSize = 'auto';
    if (el.scrollHeight > 0) el.style.blockSize = `${el.scrollHeight + 2}px`;
  }, [draft]);

  const change = (text: string) => {
    setDraft(text);
    keepReplyDraft(draftKey, text);
  };

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text || inFlight.current || state === 'sent') return;
    inFlight.current = true;
    setState('sending');
    send(text)
      .then(
        result => {
          if (!result.ok) return setState('failed');
          spendReplyDraft(draftKey);
          setDraft('');
          setState('sent');
          onSent?.();
        },
        () => setState('failed')
      )
      .finally(() => {
        inFlight.current = false;
      });
  }, [draft, draftKey, onSent, send, state]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onEscape?.();
    }
  };

  const blank = draft.trim() === '';
  const busy = state === 'sending';
  return (
    <div className={styles.reply} data-testid="quick-reply">
      <label htmlFor={id} className={styles.label}>
        {t['com.affine.pipe.director.tasks.reply.label']({ name: to })}
      </label>
      <div className={styles.line}>
        <textarea
          id={id}
          ref={box}
          rows={1}
          className={styles.input}
          value={draft}
          readOnly={busy}
          placeholder={t['com.affine.pipe.director.tasks.reply.placeholder']()}
          onChange={event => change(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button
          variant="primary"
          className={styles.send}
          disabled={blank || busy || state === 'sent'}
          onClick={submit}
        >
          {t['com.affine.pipe.director.tasks.reply.send']()}
        </Button>
      </div>
      <div className={styles.foot}>
        {state === 'sending' && (
          <p role="status" className={styles.state}>
            {t['com.affine.pipe.director.tasks.reply.sending']()}
          </p>
        )}
        {state === 'sent' && (
          <p role="status" className={styles.state}>
            {t['com.affine.pipe.director.tasks.reply.sent']()}
          </p>
        )}
        {state === 'failed' && (
          <>
            <p role="alert" className={styles.stateError}>
              {t['com.affine.pipe.director.tasks.reply.failed']()}
            </p>
            <Button
              variant="secondary"
              className={styles.footButton}
              disabled={blank}
              onClick={submit}
            >
              {t['com.affine.pipe.director.tasks.reply.retry']()}
            </Button>
          </>
        )}
        <Button variant="plain" className={styles.open} onClick={onOpen}>
          {t['com.affine.pipe.director.tasks.reply.open-task']()}
        </Button>
      </div>
    </div>
  );
}
